import { Button, Container, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type { Category } from '../../models';
import type { CategoryForm } from '../../store/forms/Admin/CategoryForm';
import { useModalContext } from '../VNModalProvider';
import VnModalBody from './VnModalBody';
import VnModalFooter from './VnModalFooter';

interface IPros {
  onSaveClick: (categoryForm: CategoryForm) => void;
  categoryForm: CategoryForm | Category;
}

const CategoryModal = ({ categoryForm, onSaveClick }: IPros) => {
  const [isDeleted, setIsDeleted] = useState(categoryForm?.isDeleted);
  const [isEnabled, setIsEnabled] = useState(categoryForm?.enabled);
  const { onClose } = useModalContext();
  const nameInput = useRef(null);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CategoryForm>({
    defaultValues: useMemo(() => {
      return categoryForm;
    }, [categoryForm]),
  });

  useEffect(() => {
    if (nameInput.current) {
      nameInput.current.focus();
    }
  }, []);

  const handleClose = () => {
    onClose();
  };

  const handleIsDeletedToggleChange = (e) => {
    const value = e.target.checked;
    setValue('isDeleted', e.target.checked);
    setIsDeleted(e.target.checked);
    if (value === true) {
      setValue('enabled', false);
      setIsEnabled(false);
    }
  };

  const handleEnabledToggleChange = (e) => {
    const formValues = getValues();
    // console.log('handleEnabledToggleChange', { formValues });
    setValue('enabled', e.target.checked);
    setIsEnabled(e.target.checked);
  };

  const onSave: SubmitHandler<CategoryForm> = async (form: CategoryForm) => {
    onSaveClick(form);
  };

  return (
    <div>
      <VnModalBody>
        <Container className="no-padding" section="component" maxWidth="lg">
          <Grid container className="section-box" spacing={2}>
            <Grid item xs={12}>
              <TextField
                inputRef={nameInput}
                variant="outlined"
                label="Category Name"
                fullWidth
                size="small"
                {...register('name', { required: true })}
                required
                error={!!errors.name}
                placeholder="Enter Category Name"
                focused
              />
              <Typography variant="inherit" className="text-danger">
                {errors.name && <span>Category Name is required</span>}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label="Priority"
                fullWidth
                size="small"
                {...register('priority', {
                  required: true,
                  pattern: {
                    value: /^[0-9]+$/,
                    message: '',
                  },
                })}
                required
                placeholder="Enter Priority"
                error={!!errors.priority}
              />
              <Typography variant="inherit" className="text-danger">
                {errors.priority && <span>Priority cannot be empty, must be an integer, and should default to 0</span>}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    className="fuel-toggle"
                    sx={{ marginTop: `-1px`, marginLeft: `5px` }}
                    inputProps={{ 'aria-label': '' }}
                    checked={isEnabled}
                    onChange={(e) => handleEnabledToggleChange(e)}
                    size="small"
                    disabled={isDeleted}
                  />
                }
                label="Enabled"
                labelPlacement="start"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    className="fuel-toggle"
                    sx={{ marginTop: `-1px`, marginLeft: `5px` }}
                    inputProps={{ 'aria-label': '' }}
                    checked={isDeleted}
                    onChange={(e) => handleIsDeletedToggleChange(e)}
                    size="small"
                    disabled={getValues('allowDelete') === false}
                  />
                }
                label="Deleted"
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </Container>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            <Button color="success" onClick={handleSubmit(onSave)} autoFocus variant="contained">
              Save
            </Button>
            <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
              Cancel
            </Button>
          </>
        }
      />
    </div>
  );
};

export default CategoryModal;
